// RC1.2.0 Sprint D — Top-right notification bell.
//
// • Standalone bell icon, never in the BottomNav (founder rule).
// • Shows the unread count badge from /api/notifications/unread-count.
// • Tap → navigates to /notifications.
// • Polls every 90s while the app is open + refreshes on tab visibilitychange.
// • Hidden when the user is logged out.
// • Updates the App Badge API where supported.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/apiClient';

const POLL_INTERVAL_MS = 90_000;

const setAppBadge = (count) => {
  try {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      if (count > 0) navigator.setAppBadge(count).catch(() => {});
      else navigator.clearAppBadge?.().catch(() => {});
    }
  } catch { /* unsupported — fail silently */ }
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.get('/api/notifications/unread-count');
      const n = typeof data?.unread_count === 'number' ? data.unread_count : 0;
      setUnread(n);
      setAppBadge(n);
    } catch { /* ignore — bell stays at last known count */ }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      setAppBadge(0);
      return undefined;
    }
    fetchUnread();
    const interval = setInterval(fetchUnread, POLL_INTERVAL_MS);
    const onVisible = () => { if (!document.hidden) fetchUnread(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchUnread);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchUnread);
    };
  }, [user, fetchUnread]);

  if (!user) return null;

  const badge = unread > 99 ? '99+' : (unread > 0 ? String(unread) : null);

  return (
    <button
      type="button"
      onClick={() => navigate('/notifications')}
      aria-label={badge ? `Notifications — ${badge} unread` : 'Notifications'}
      data-testid="notification-bell"
      className="fixed top-3 right-3 md:top-4 md:right-6 z-40 w-11 h-11 rounded-full bg-white/95 backdrop-blur border border-[#E5E4E2] flex items-center justify-center shadow-sm hover:border-[#0D0D0D] transition-colors">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D0D0D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {badge && (
        <span
          data-testid="notification-bell-badge"
          className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 bg-[#C7A96B] text-white rounded-full font-sans text-[10px] font-medium flex items-center justify-center border-2 border-white">
          {badge}
        </span>
      )}
    </button>
  );
};

export default NotificationBell;
